import ScrollTable from "../ui/ScrollableTable";
import React, {useEffect, useState} from "react";
import {BidBaseModel} from "../bid/bidding_model";
import {fetchWithToken, postWithToken} from "../../api/apiBase";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {useAuthInfo} from "@propelauth/react";
import {dateFormatter, formatDate, moneyFormatter} from "../../lib/formatters";
import {MultiSelectCombobox} from "../ui/comboBox";
import { Button } from "../ui/button";
import { CopyButton2 } from "../exportData/CopyButton";
import { copyTableWihoutHeaderColumnToClipboard } from "../project_conf/datasheetHelpers";


enum CellType {
    dollar = "$",
    number = "num",
    date = "date",
    text = "txt",
}


type FieldCell = {
    value: any;
    pos: number[];
    type: CellType;
}

type CompRow = {
    labels: string[];
    values: { string: FieldCell};
    min?: number;
    max?: number;
}


type CompareBidsProps = {
    bids: BidBaseModel[];
    config: any;
    rows: CompRow[];
}



function BidCompHeader(key: string, bids: BidBaseModel[], type: string, label:string){
    return {
        labels: [label],
        values: Object.fromEntries(
            // @ts-ignore
            bids.map((bid) => [bid.id, {value: bid[key], type:type}])
        ),
        min: Math.min(...bids.map(x=>x.bid_amount)),
        max: Math.max(...bids.map(x=>x.bid_amount))
    }
}


function FormattedCell(cell: FieldCell, orig: any){
    if (cell.type === CellType.dollar){
        if(!cell.value){
            return <></>
        }
        var hue = Math.floor((1 - (cell.value- orig.min) / (orig.max - orig.min)) * 100);
        return <span style={{background:"hsl("+ hue+",50%,80%)"}} >
            {moneyFormatter.format(cell.value)}</span>

    } else if (cell.type === CellType.date) {
        return <>{formatDate(cell.value) }</>

    }else if (cell.type === CellType.text) {
        return <>{cell.value}</>
    }
    return <></>
}

function HeaderCell(bid: BidBaseModel, navigate: (x:string)=> void){
    return <a className="text-wrap text-sm"
              onClick={() => navigate(`/project/${bid.project_id}/bid/${bid.id}`)}>
        {bid.company_name + " Bid #" + String(bid.number)}
    </a>
}


function bidToDropdown(bid: BidBaseModel){
    return {
        value: (bid as any)._id ,
        label: `#${bid.number} - ${bid.company_name} ${moneyFormatter.format(bid.bid_amount)}`
    }
}

/**
 *
 *
 * */
function CompareBidsView({config, bids, rows}: CompareBidsProps){
    // const [tableData, setTableData] = useState([]);
    // const [columns, setColumns] = useState([])
    const navigate = useNavigate();
    const [colOrder, setColOrder] = useState([]);

    const buildCols = () => {
        let cols: any = [];
        // definition columns
        const num_label_cols = Math.max(...rows.map(row => row.labels.length));

        for (let i = 0; i < num_label_cols; i++) {
            cols.push({
                key: i,
                header: "",
                cell: (row: any) => {
                    if(row.original.labels.length > i){
                        return <div>{row.original.labels[i]}</div>
                    }
                }
            })
        }

        // input columns
        for (let i = 0; i < bids.length; i++) {
            let bid = bids[i]
            const def = {
                key: bid.id,
                header: HeaderCell(bid, navigate),
                cell: (row: any) => {
                    if (!row.original.values[bid.id]){
                        return <div>N/A</div>
                    }
                    let item = row.original.values[bid.id]
                    return FormattedCell(item, row.original);

                }
            }
            cols.push(def)
        }
        // setColumns(cols)
        return cols;
    }

    const buildRows = () => {
        //
        return [

            BidCompHeader('company_name', bids, "txt" , "Company"),
            BidCompHeader('bid_amount', bids, "$" , "Total Amount"),
            BidCompHeader('submitted_at', bids, "date", "Submitted"),
            BidCompHeader('response_status', bids, "txt", "Response"),
            BidCompHeader(' ', bids, "", ""),
            ...rows
        ]
    }

    const formatData = () => {
        const cols = buildCols()
        const rows = buildRows()

        const headers = cols.map((col: any) => {
            if (typeof col.header === "function") {
                // const bid = bids.find(b => b.id === col.key);
                // return bid ? bid.company_name + " Bid #" + String(bid.number) : "";
            }
            if (col.header && col.header.props && col.header.props.children) {
                return col.header.props.children;
            }
            return col.header || "";
        });

        
        const dataRows = rows.map(row => {
            return cols.map((col: any) => {
                if (col.cell && typeof col.cell === "function") {
                    const cellContent = col.cell({ original: row });
                    if (cellContent) {
                        if (cellContent.props && cellContent.props.children) {
                            return cellContent.props.children.toString().replace(/\n/g, " ");
                        }
                    }
                }
                return ""; 
            }); 
        }); 
        return [headers, ...dataRows];
    }
    return <>
        <div className="max-w-full text-sm relative">
            <div className="absolute -top-11 right-20 z-10">
        <CopyButton2 dataTransform={formatData} executeCustomCopy={copyTableWihoutHeaderColumnToClipboard}/>
            </div>
            <ScrollTable
                columns={buildCols()}
                data={buildRows() }
                maxHeight=""/>
        </div>
    </>
}




/**
 * /project/{project_id}/bidding/compare
 * */
function CompareBids(){
    const navigate = useNavigate();
    const {state} = useLocation()
    const {project_id} = useParams();
    const {accessToken} = useAuthInfo();
    const [data, setData] = useState<CompareBidsProps | null>(null)
    const [bids, setBids] = useState<{value:string, label:string}[]>([])
    const [selectedBids, setSelectedBids] = useState([]);
    const [isStateLoaded, setIsStateLoaded] = useState(false);
    const [isFromMultiSelect, setIsFromMultiSelect] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(()=> {
        if (state && state.ids) {
        setSelectedBids(state.ids);
        }
        setIsStateLoaded(true)
    }, [state])

    useEffect(() => {
        fetchWithToken(
            `/projects/${project_id}/bidding/`,
            accessToken as string)
            .then((data: BidBaseModel[]) => {
                setBids(data.map(bid => bidToDropdown(bid)))

            })
            .catch((err) => {
                console.log('err on get bids', err);
            })
    }, [project_id])

    useEffect(() => {
        if (!isStateLoaded) return
        let query = null
        if(selectedBids.length === 0){
            query = {latest: true}
        } else {
            query = {ids: selectedBids, latest: false}
        }
        console.log('query', query)
        postWithToken(`/projects/${project_id}/developer/compare`,
            JSON.stringify(query),
            accessToken as string)
            .then((data) => {
                // console.log(data, 'data');
                setData(data)
                // @ts-ignore
                setSelectedBids(data.bids.map(bid => bid.id))
            })
            .catch((err) => {
                console.log('err', JSON.stringify(err));
            })
    }, [project_id, isStateLoaded, isFromMultiSelect])

    const handleValueChange = (dx:any) => {
        console.log(dx)
        setSelectedBids(dx)
        setIsFromMultiSelect((prev) => !prev)
    }

    if(!data){ return <></>}
    if(!bids){ return <></>}

    return <div >
        <div className="w-full flex justify-between mb-2">
            <div className="">
        <MultiSelectCombobox
              width="w-[250px]"
              types={  bids }
              open={isOpen}
              setOpen={() => setIsOpen((value) => !value)}
              value={ selectedBids}
              displayValue="select more"
              onChange={(value) => {
                  handleValueChange(value);
              }}
            />
                </div>

        </div>
        <CompareBidsView
            bids={data.bids}
            config={data.config}
            rows={data.rows}
        />
    </div>
}


export default CompareBids

